import { api } from '../../shared/api/client';
import {
  type ListEnvelope,
  normalizeListEnvelope,
  toGenexusParams,
} from '../../shared/api/genexus-query';
import type { CrudApi } from '../../shared/api/crud-hooks';
import type { DataTableQuery } from '../../shared/components/DataTable';
import type { EmployeeDto, EmployeeInput, EmployeeListItem, EmployeePasswordInput } from './types';

const BASE = '/v1/employees';

/**
 * CRUD principal + 4 ações de login GAM. Cada ação retorna o DTO atualizado
 * (com `statusLogin` refletindo o novo estado), exceto `password` que não muda
 * `statusLogin`.
 */
export const employeesApi: CrudApi<EmployeeListItem, EmployeeDto, EmployeeInput> & {
  loginActivate: (id: number) => Promise<EmployeeDto>;
  loginBlock: (id: number) => Promise<EmployeeDto>;
  loginUnblock: (id: number) => Promise<EmployeeDto>;
  loginPassword: (id: number, input: EmployeePasswordInput) => Promise<EmployeeDto>;
} = {
  async list(query: DataTableQuery): Promise<ListEnvelope<EmployeeListItem>> {
    const { data } = await api.get<{
      data: EmployeeListItem[];
      meta: { total: number; pagina?: number; porPagina?: number };
    }>(BASE, { params: toGenexusParams(query) });
    return normalizeListEnvelope(data);
  },

  async get(id: number): Promise<EmployeeDto> {
    const { data } = await api.get<{ data: EmployeeDto }>(`${BASE}/${id}`);
    return data.data;
  },

  async create(input: EmployeeInput): Promise<EmployeeDto> {
    const { data } = await api.post<{ data: EmployeeDto }>(BASE, input);
    return data.data;
  },

  async update(id: number, input: EmployeeInput): Promise<EmployeeDto> {
    const { data } = await api.put<{ data: EmployeeDto }>(`${BASE}/${id}`, input);
    return data.data;
  },

  async remove(id: number): Promise<void> {
    // DELETE = demissão (seta dataDemissao=today). BFF devolve 200 com DTO atualizado
    // mas o CrudApi.remove é `Promise<void>` — ignoramos o body.
    await api.delete(`${BASE}/${id}`);
  },

  async loginActivate(id: number): Promise<EmployeeDto> {
    const { data } = await api.post<{ data: EmployeeDto }>(`${BASE}/${id}/login/activate`);
    return data.data;
  },

  async loginBlock(id: number): Promise<EmployeeDto> {
    const { data } = await api.post<{ data: EmployeeDto }>(`${BASE}/${id}/login/block`);
    return data.data;
  },

  async loginUnblock(id: number): Promise<EmployeeDto> {
    const { data } = await api.post<{ data: EmployeeDto }>(`${BASE}/${id}/login/unblock`);
    return data.data;
  },

  async loginPassword(id: number, input: EmployeePasswordInput): Promise<EmployeeDto> {
    const { data } = await api.post<{ data: EmployeeDto }>(`${BASE}/${id}/login/password`, input);
    return data.data;
  },
};
